import { useState } from 'react'
import { ExternalLink, FileText, Image as ImageIcon, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { apiClient } from '@/lib/api/client'
import type { InquiryDocumentArtifact, InquiryFiledDocument } from '@/lib/api/types'

/** Files a client emailed, which triage filed on the inquiry. */
export function clientFiles(documents: InquiryDocumentArtifact[] | null | undefined) {
  return (documents ?? []).filter(
    (document) =>
      document.type === 'CLIENT_ATTACHMENT' && typeof document.message_attachment_id === 'string',
  )
}

function size(bytes: unknown) {
  if (typeof bytes !== 'number' || bytes <= 0) return null
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/** Business time is Kampala time, wherever the reader is. */
function received(iso: unknown) {
  if (typeof iso !== 'string') return null
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    timeZone: 'Africa/Kampala',
  })
}

/** "COMMERCIAL_INVOICE" → "Commercial invoice". */
function documentLabel(type: string) {
  const words = type.toLowerCase().replaceAll('_', ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

function metadataText(document: InquiryFiledDocument, key: string) {
  const value = document.metadata?.[key]
  return typeof value === 'string' ? value : null
}

interface FileRow {
  key: string
  name: string
  isImage: boolean
  details: string[]
  access: () => Promise<{ access: { url: string } }>
}

/**
 * What the client sent, and the documents filed on this inquiry: files they
 * emailed, the invoice from the website's quote form, and papers filed from
 * Beacon. Opening one asks the API for a short-lived link, so no file is ever
 * public.
 */
export function ClientFiles({
  documents,
  filedDocuments,
  inquiryId,
}: {
  documents: InquiryDocumentArtifact[] | null | undefined
  filedDocuments?: InquiryFiledDocument[] | null
  inquiryId?: string
}) {
  const [opening, setOpening] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const rows: FileRow[] = [
    ...(inquiryId ? (filedDocuments ?? []) : []).map((document) => {
      const label = documentLabel(document.type)
      return {
        key: `document-${document.id}`,
        name: document.file_name || label,
        isImage: String(metadataText(document, 'content_type') ?? '').startsWith('image/'),
        details: [
          label,
          metadataText(document, 'source') === 'WEBSITE_QUOTE_FORM' ? 'Website quote form' : null,
          size(document.file_size_bytes),
          received(document.created_at),
        ].filter((detail): detail is string => Boolean(detail)),
        access: () => apiClient.getInquiryDocumentAccess(inquiryId!, document.id),
      }
    }),
    ...clientFiles(documents).map((file) => {
      const id = file.message_attachment_id as string
      return {
        key: `attachment-${id}`,
        name: file.file_name || 'Attachment',
        isImage: String(file.content_type ?? '').startsWith('image/'),
        details: [size(file.size_bytes), received(file.uploaded_at)].filter(
          (detail): detail is string => Boolean(detail),
        ),
        access: () => apiClient.getAttachmentAccess(id),
      }
    }),
  ]

  if (rows.length === 0) return null

  async function open(row: FileRow) {
    setOpening(row.key)
    setError(null)
    // Opened now, while the click still counts, so the browser does not block it.
    const tab = window.open('', '_blank')
    try {
      const { access } = await row.access()
      if (tab) tab.location.href = access.url
      else window.location.assign(access.url)
    } catch (caught) {
      tab?.close()
      setError(caught instanceof Error ? caught.message : 'The file could not be opened')
    } finally {
      setOpening(null)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Files</CardTitle>
        <CardDescription>
          What the client sent, and documents filed on this inquiry.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <ul className="divide-y rounded-md border" aria-label="Files">
          {rows.map((row) => {
            const Icon = row.isImage ? ImageIcon : FileText
            return (
              <li key={row.key} className="flex items-center gap-3 px-3 py-2 text-sm">
                <Icon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{row.name}</div>
                  {row.details.length > 0 && (
                    <div className="text-xs text-muted-foreground">{row.details.join(' · ')}</div>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="xs"
                  onClick={() => void open(row)}
                  disabled={opening === row.key}
                  aria-label={`Open ${row.name}`}
                >
                  {opening === row.key ? <Loader2 className="animate-spin" /> : <ExternalLink />}
                  Open
                </Button>
              </li>
            )
          })}
        </ul>
        {error && <p className="text-sm text-destructive">{error}</p>}
      </CardContent>
    </Card>
  )
}
